import * as React from 'react';
import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import {
  AgentCallAgendaSubscription, useAgent,
  useAgentCallAgendaSubscription,
  useAgentCallAgendaWholeStateSubscription
} from '../api';
import { AgentCallAgendaTaskOutput, CurrentAgentCallAgendaStateOutput } from '@coverright/data-access/types/enrollment';
import { keycloak } from '@coverright/shared/keycloak';
import { useHandledSubscription } from './use-handled-subscription';

type Type = AgentCallAgendaSubscription['agentCallAgenda'] & {
  state?: CurrentAgentCallAgendaStateOutput,
  currentClient?: AgentCallAgendaTaskOutput,
  licensedStates: string[]
}

const defaultValue: Type = {
  assignedClientsCallTasks: [],
  dontDisturb: false,
  scheduledReassignAt: undefined,
  licensedStates: [],
}

const AgentCallAgendaContext = createContext<Type>(defaultValue)

export const AgentCallAgendaProvider = ({children}: PropsWithChildren) => {
  const {data} = useHandledSubscription(useAgentCallAgendaSubscription());
  const {data: wholeStateData} = useHandledSubscription(useAgentCallAgendaWholeStateSubscription());
  const agent = useAgent();

  const value = useMemo(() => {
    const v = data?.agentCallAgenda || defaultValue;

    const currentClient = v.assignedClientsCallTasks.length ?
      wholeStateData?.agentCallAgendaWholeState.tasks.find(t => t.currentAssignment?.assignedAgentId === agent?.id) :
      undefined

    return {
      ...v,
      currentClient,
      state: wholeStateData?.agentCallAgendaWholeState,
      licensedStates: wholeStateData?.agentCallAgendaWholeState.agentsSorted
        .find(a => a.agentId === keycloak.tokenParsed?.sub)?.licensedStates?.split(',') || []
    }
  }, [data, wholeStateData, agent?.id])

  return <AgentCallAgendaContext.Provider value={value}>
    {children}
  </AgentCallAgendaContext.Provider>
}

export const useAgentCallAgenda = () => useContext(AgentCallAgendaContext);
